import { create } from 'zustand';
import { RawShopEntry, groupAndOrderShopSections, parseRawEntryItem } from '../utils/fortniteShopParser';
import { SupportedCurrency, ShopItem } from '../types';
import { fetchRawShopEntries, getLocalCachedShop, getCacheApiShop } from '../services/shopApi';
import { useCurrencyStore } from './useCurrencyStore';
import { sanitizeCurrency, calculateFiatPrice } from '../utils/currency';

export interface CategoryCountSummary {
  TODOS: number;
  SKINS: number;
  GESTOS: number;
  PICOS: number;
  MOCHILAS: number;
  LOTES: number;
}

export function matchesCategory(item: any, categoryFilter: string): boolean {
  if (!categoryFilter) return true;
  const filter = categoryFilter.trim().toUpperCase();
  if (filter === 'ALL' || filter === 'TODOS' || filter === 'TODAS') return true;
  const itemCat = (item.category || '').trim().toUpperCase();
  if (itemCat === filter) return true;
  if ((filter === 'LOTES' || filter === 'BUNDLES') && (item.isBundle || itemCat.includes('LOTE') || itemCat.includes('BUNDLE'))) return true;
  if ((filter === 'SKINS' || filter === 'TRAJES') && (itemCat.includes('SKIN') || itemCat.includes('TRAJE') || itemCat.includes('OUTFIT') || itemCat.includes('PERSONAJE'))) return true;
  if ((filter === 'PICOS' || filter === 'HERRAMIENTAS') && (itemCat.includes('PICO') || itemCat.includes('HERRAMIENTA') || itemCat.includes('PICKAXE'))) return true;
  if ((filter === 'GESTOS' || filter === 'BAILES') && (itemCat.includes('GESTO') || itemCat.includes('BAILE') || itemCat.includes('EMOTE'))) return true;
  if ((filter === 'MOCHILAS' || filter === 'ACCESORIOS') && (itemCat.includes('MOCHILA') || itemCat.includes('ACCESORIO') || itemCat.includes('BACKPACK'))) return true;
  return false;
}

export function matchesSearch(item: any, query: string): boolean {
  if (!query || !query.trim()) return true;
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const searchableText = [
    item.name,
    item.description,
    item.category,
    item.section,
    item.rarity,
    ...(item.tags || []),
  ].filter(Boolean).join(' ').toLowerCase();
  return terms.every((term) => searchableText.includes(term));
}

export function matchesSection(item: any, sectionFilter: string): boolean {
  if (!sectionFilter) return true;
  const norm = sectionFilter.trim().toUpperCase();
  if (norm === 'TODAS' || norm === 'TODOS' || norm === 'ALL') return true;
  return (item.section || '').trim().toUpperCase() === norm;
}

export interface ShopStoreState {
  rawEntries: RawShopEntry[];
  items: ShopItem[]; // Backward compatibility for cart / selectors
  isLoading: boolean;
  error: string | null;
  isFallback: boolean;
  activeCurrency: SupportedCurrency;
  activeCategory: string;
  searchQuery: string;
  selectedSectionIndex: number;

  // Actions
  fetchShop: (currency?: SupportedCurrency, force?: boolean) => Promise<void>;
  setItems: (items: ShopItem[]) => void;
  recalculatePrices: (currency: SupportedCurrency) => void;
  setActiveCategory: (category: string) => void;
  setSearchQuery: (query: string) => void;
  setSelectedSectionIndex: (index: number) => void;
  resetFilters: () => void;

  // Selectors
  getOrderedSections: () => [string, RawShopEntry[]][];
  getCategoryCounts: () => CategoryCountSummary;
  getFilteredItems: (categoryFilter?: string, query?: string) => ShopItem[];
  getItemByOfferId: (offerId: string) => RawShopEntry | undefined;
}

let activeAbortController: AbortController | null = null;

export const useShopStore = create<ShopStoreState>()((set, get) => {
  // Sync currency from useCurrencyStore
  if (typeof window !== 'undefined') {
    useCurrencyStore.subscribe((currState, prevCurrState) => {
      if (currState.currency !== prevCurrState.currency) {
        set({ activeCurrency: currState.currency });
      }
    });
  }

  const initialCurrency = useCurrencyStore.getState().currency;
  const initialCache = typeof window !== 'undefined' ? getLocalCachedShop() : null;
  const initialEntries = initialCache?.entries || [];
  const initialLegacyItems: ShopItem[] = initialEntries.length > 0
    ? (initialEntries
        .map((entry) => {
          const parsed = parseRawEntryItem(entry);
          if (!parsed) return null;
          const price = calculateFiatPrice(entry.finalPrice, initialCurrency, entry.offerId);
          return {
            id: entry.offerId,
            name: parsed.name,
            description: parsed.description || undefined,
            vbucks: entry.finalPrice,
            finalPrice: entry.finalPrice,
            price,
            currency: initialCurrency,
            image: parsed.image,
            rarity: parsed.rarity?.displayValue || 'Raro',
            category: parsed.isBundle ? 'LOTES' : parsed.type?.displayValue || 'OBJETO',
            section: entry.layout?.name || entry.layout?.category || 'Destacados',
            isBundle: parsed.isBundle,
          };
        })
        .filter(Boolean) as ShopItem[])
    : [];

  return {
    rawEntries: initialEntries,
    items: initialLegacyItems,
    isLoading: initialEntries.length === 0,
    error: null,
    isFallback: false,
    activeCurrency: initialCurrency,
    activeCategory: 'ALL',
    searchQuery: '',
    selectedSectionIndex: 0,

    fetchShop: async (currency?: SupportedCurrency, force?: boolean) => {
      const targetCurrency = sanitizeCurrency(currency ?? useCurrencyStore.getState().currency);
      const currentEntries = get().rawEntries;

      // Si no tenemos entradas en memoria, intentar leer de Cache API antes de mostrar skeleton
      if (currentEntries.length === 0) {
        const cacheApiData = await getCacheApiShop();
        if (cacheApiData?.entries && cacheApiData.entries.length > 0) {
          const legacyItems = cacheApiData.entries
            .map((entry) => {
              const parsed = parseRawEntryItem(entry);
              if (!parsed) return null;
              const price = calculateFiatPrice(entry.finalPrice, targetCurrency, entry.offerId);
              return {
                id: entry.offerId,
                name: parsed.name,
                description: parsed.description || undefined,
                vbucks: entry.finalPrice,
                finalPrice: entry.finalPrice,
                price,
                currency: targetCurrency,
                image: parsed.image,
                rarity: parsed.rarity?.displayValue || 'Raro',
                category: parsed.isBundle ? 'LOTES' : parsed.type?.displayValue || 'OBJETO',
                section: entry.layout?.name || entry.layout?.category || 'Destacados',
                isBundle: parsed.isBundle,
              };
            })
            .filter(Boolean) as ShopItem[];

          set({
            rawEntries: cacheApiData.entries,
            items: legacyItems,
            isLoading: false,
          });
        } else {
          // Si realmente no hay nada en caché, mostramos el skeleton inicial
          set({ isLoading: true, error: null });
        }
      }

      if (activeAbortController) {
        activeAbortController.abort();
      }
      activeAbortController = new AbortController();

      try {
        const result = await fetchRawShopEntries(targetCurrency, {
          signal: activeAbortController.signal,
        });

        if (result.entries && result.entries.length > 0) {
          const nowEntries = get().rawEntries;
          const isSameEntries =
            nowEntries.length === result.entries.length &&
            nowEntries[0]?.offerId === result.entries[0]?.offerId &&
            nowEntries[nowEntries.length - 1]?.offerId === result.entries[result.entries.length - 1]?.offerId;

          if (!isSameEntries || force || get().activeCurrency !== targetCurrency) {
            const legacyItems: ShopItem[] = result.entries
              .map((entry) => {
                const parsed = parseRawEntryItem(entry);
                if (!parsed) return null;
                const price = calculateFiatPrice(entry.finalPrice, targetCurrency, entry.offerId);
                return {
                  id: entry.offerId,
                  name: parsed.name,
                  description: parsed.description || undefined,
                  vbucks: entry.finalPrice,
                  finalPrice: entry.finalPrice,
                  price,
                  currency: targetCurrency,
                  image: parsed.image,
                  rarity: parsed.rarity?.displayValue || 'Raro',
                  category: parsed.isBundle ? 'LOTES' : parsed.type?.displayValue || 'OBJETO',
                  section: entry.layout?.name || entry.layout?.category || 'Destacados',
                  isBundle: parsed.isBundle,
                };
              })
              .filter(Boolean) as ShopItem[];

            set({
              rawEntries: result.entries,
              items: legacyItems,
              isLoading: false,
              error: null,
              isFallback: result.isFallback,
              activeCurrency: targetCurrency,
            });
          } else {
            if (get().isLoading) {
              set({ isLoading: false });
            }
          }
        } else if (get().rawEntries.length === 0) {
          set({
            isLoading: false,
            error: result.error || 'No se pudo cargar la tienda',
          });
        }
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        if (get().rawEntries.length === 0) {
          set({
            isLoading: false,
            error: err?.message || 'Error cargando la tienda',
          });
        }
      } finally {
        activeAbortController = null;
      }
    },

    setItems: (items: ShopItem[]) => {
      set({ items });
    },

    recalculatePrices: (currency: SupportedCurrency) => {
      const safeCurr = sanitizeCurrency(currency);
      const updatedItems = get().items.map((item) => ({
        ...item,
        currency: safeCurr,
        price: calculateFiatPrice(item.vbucks, safeCurr, item.id),
      }));
      set({ items: updatedItems, activeCurrency: safeCurr });
    },

    setActiveCategory: (category: string) => {
      set({ activeCategory: category.toUpperCase() });
    },

    setSearchQuery: (query: string) => {
      set({ searchQuery: query });
    },

    setSelectedSectionIndex: (index: number) => {
      set({ selectedSectionIndex: index });
    },

    resetFilters: () => {
      set({ activeCategory: 'ALL', searchQuery: '', selectedSectionIndex: 0 });
    },

    getOrderedSections: () => {
      const state = get();
      const allSections = groupAndOrderShopSections(state.rawEntries);

      const categoryFilter = state.activeCategory.trim().toUpperCase();
      const isAllCategory = categoryFilter === 'ALL' || categoryFilter === 'TODOS';
      const searchTerms = state.searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);

      // If no category and no search, return full ordered sections
      if (isAllCategory && searchTerms.length === 0) {
        return allSections;
      }

      // Filter entries within each section
      const filtered: [string, RawShopEntry[]][] = [];

      for (const [secName, entries] of allSections) {
        const matchingEntries = entries.filter((entry) => {
          const item = parseRawEntryItem(entry);
          if (!item) return false;

          // 1. Category check
          if (!isAllCategory) {
            const isBundle = item.isBundle || Boolean(entry.bundle);
            if (categoryFilter === 'LOTES' && !isBundle) return false;
            if (categoryFilter === 'SKINS') {
              const isSkin =
                item.type?.value === 'outfit' ||
                item.type?.backendValue === 'AthenaCharacter' ||
                entry.brItems?.some((b) => b.type?.value === 'outfit');
              if (!isSkin) return false;
            }
            if (categoryFilter === 'PICOS') {
              const isPico =
                item.type?.value === 'pickaxe' ||
                item.type?.backendValue === 'AthenaPickaxe' ||
                entry.brItems?.some((b) => b.type?.value === 'pickaxe');
              if (!isPico) return false;
            }
            if (categoryFilter === 'GESTOS') {
              const isGesto =
                item.type?.value === 'emote' ||
                item.type?.backendValue === 'AthenaDance' ||
                entry.brItems?.some((b) => b.type?.value === 'emote');
              if (!isGesto) return false;
            }
            if (categoryFilter === 'MOCHILAS') {
              const isMochila =
                item.type?.value === 'backpack' ||
                item.type?.backendValue === 'AthenaBackpack' ||
                entry.brItems?.some((b) => b.type?.value === 'backpack');
              if (!isMochila) return false;
            }
          }

          // 2. Search check
          if (searchTerms.length > 0) {
            const textToSearch = [
              item.name,
              item.description,
              secName,
              entry.devName,
              ...(entry.brItems || []).map((b) => b.name),
              ...(entry.tracks || []).map((t) => `${t.title} ${t.artist}`),
            ]
              .filter(Boolean)
              .join(' ')
              .toLowerCase();

            const matchesAllTerms = searchTerms.every((term) => textToSearch.includes(term));
            if (!matchesAllTerms) return false;
          }

          return true;
        });

        if (matchingEntries.length > 0) {
          filtered.push([secName, matchingEntries]);
        }
      }

      return filtered;
    },

    getCategoryCounts: () => {
      const entries = get().rawEntries;
      if (entries && entries.length > 0) {
        let skins = 0;
        let gestos = 0;
        let picos = 0;
        let mochilas = 0;
        let lotes = 0;

        for (const entry of entries) {
          const item = parseRawEntryItem(entry);
          if (!item) continue;
          if (item.isBundle || entry.bundle) {
            lotes++;
          } else if (
            item.type?.value === 'outfit' ||
            item.type?.backendValue === 'AthenaCharacter' ||
            entry.brItems?.some((b) => b.type?.value === 'outfit')
          ) {
            skins++;
          } else if (
            item.type?.value === 'pickaxe' ||
            item.type?.backendValue === 'AthenaPickaxe' ||
            entry.brItems?.some((b) => b.type?.value === 'pickaxe')
          ) {
            picos++;
          } else if (
            item.type?.value === 'emote' ||
            item.type?.backendValue === 'AthenaDance' ||
            entry.brItems?.some((b) => b.type?.value === 'emote')
          ) {
            gestos++;
          } else if (
            item.type?.value === 'backpack' ||
            item.type?.backendValue === 'AthenaBackpack' ||
            entry.brItems?.some((b) => b.type?.value === 'backpack')
          ) {
            mochilas++;
          }
        }

        return {
          TODOS: entries.length,
          SKINS: skins,
          GESTOS: gestos,
          PICOS: picos,
          MOCHILAS: mochilas,
          LOTES: lotes,
        };
      }

      // Fallback for tests or direct items population
      const items = get().items;
      let skins = 0;
      let gestos = 0;
      let picos = 0;
      let mochilas = 0;
      let lotes = 0;

      for (const item of items) {
        if (matchesCategory(item, 'LOTES')) {
          lotes++;
        } else if (matchesCategory(item, 'SKINS')) {
          skins++;
        } else if (matchesCategory(item, 'PICOS')) {
          picos++;
        } else if (matchesCategory(item, 'GESTOS')) {
          gestos++;
        } else if (matchesCategory(item, 'MOCHILAS')) {
          mochilas++;
        }
      }

      return {
        TODOS: items.length,
        SKINS: skins,
        GESTOS: gestos,
        PICOS: picos,
        MOCHILAS: mochilas,
        LOTES: lotes,
      };
    },

    getFilteredItems: (categoryFilter?: string, query?: string) => {
      const cat = categoryFilter ?? get().activeCategory;
      const q = query ?? get().searchQuery;
      return get().items.filter((item) => matchesCategory(item, cat) && matchesSearch(item, q));
    },

    getItemByOfferId: (offerId: string) => {
      return get().rawEntries.find((entry) => entry.offerId === offerId);
    },
  };
});

export default useShopStore;
